import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../../shared/constant";
import { toast } from "react-toastify";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Loader from "../../shared/loader";
import { isPWA, isMobilePWA, getPWADisplayMode } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import "../../styles/pwa-customers.css";

const Invoices = () => {
  const [searchValue, setSearchValue] = useState("");
  const [partyData, setPartyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwaMode, setPwaMode] = useState(false);
  const [mobilePWA, setMobilePWA] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [webViewMode, setWebViewMode] = useState(false);

  const getInvoices = (page = 1) => {
    axios.post(API_URL.INVOICE_LIST, { page: page, limit: 30 }).then(
      (res) => {
        if (res.data.status === 1) {
          res.data.data.data.map(
            (item) => (item.date = moment(item.date).format("DD/MM/YYYY"))
          );
          setPartyData(res.data.data.data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        toast.error("Something went wrong..", {
          autoClose: 3000,
          
        });
      }
    );
  };

  const deleteInvoice = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Invoice No - " + row.invoice_no,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.post(API_URL.INVOICE_DELETE, { id: row.id }).then(
          (res) => {
            if (res.data.status === 1) {
              let newData = partyData.filter((r) => r.id != row.id);
              setPartyData(newData);
            }
            toast.success(row.name + " Delete Successfully", { autoClose: 3000 });
            setLoading(false);
          },
          (error) => {
            setLoading(false);
            toast.error("Something went wrong..", { autoClose: 3000 });
          }
        );
      }
    });
  };

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (pwaMode) {
        toast.success("Connection restored", { autoClose: 2000 });
        getInvoices(); // Refresh data when back online
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      if (pwaMode) {
        toast.warning("You're offline. Some features may not work.", { autoClose: 3000 });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pwaMode]);

  // Pull to refresh functionality
  useEffect(() => {
    if (!pwaMode || !mobilePWA) return;

    let startY = 0;
    let currentY = 0;
    let isPulling = false;
    let pullElement = null;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        pullElement = document.querySelector('.pwa-invoice-list');
      }
    };

    const handleTouchMove = (e) => {
      if (window.scrollY === 0 && pullElement) {
        currentY = e.touches[0].clientY;
        const pullDistance = currentY - startY;

        if (pullDistance > 50) {
          isPulling = true;
          pullElement.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
          pullElement.style.opacity = '0.7';
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPulling && pullElement) {
        pullElement.style.transform = '';
        pullElement.style.opacity = '';
        
        setRefreshing(true);
        getInvoices().finally(() => {
          setRefreshing(false);
          toast.success("Data refreshed", { autoClose: 1500 });
        });
        
        isPulling = false;
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pwaMode, mobilePWA]);

  useEffect(() => {
    getInvoices();
    // Check PWA mode, mobile, and webview
    setPwaMode(isPWA());
    setMobilePWA(isMobilePWA());
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    setWebViewMode(isWebView());
  }, []);
  return (
    <>
      {(pwaMode || (isMobile && !webViewMode)) && (
        <div className="pwa-header bg-primary text-white p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              <i className="simple-icon-doc me-2"></i>
              Invoices ({partyData.length})
            </h4>
            <div className="d-flex align-items-center gap-2">
              {!isOnline && (
                <div className="pwa-status-indicator bg-warning">
                  <i className="simple-icon-wifi me-1"></i>
                  Offline
                </div>
              )}
              {mobilePWA && (
                <div className="pwa-status-indicator">
                  <i className="simple-icon-screen-smartphone me-1"></i>
                  PWA
                </div>
              )}
              {refreshing && (
                <div className="pwa-status-indicator bg-info">
                  <i className="simple-icon-refresh me-1"></i>
                  Refreshing
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className={`row ${(pwaMode || (isMobile && !webViewMode)) ? 'pwa-content' : ''}`}>
        {!(pwaMode || (isMobile && !webViewMode)) && (
          <>
            <div className="col-md-6">
              <h1>Invoices <small className="me-2">({partyData.length})</small></h1>
              {webViewMode && (
                <small className="text-muted">
                  <i className="simple-icon-screen-smartphone me-1"></i>
                  WebView Mode
                </small>
              )}
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center  justify-content-end">
                <div className="form-group mb-0 mr-2" style={{width: 300}}>
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="form-control"
                    placeholder="Search"
                  />
                </div>
                <Link to={"/sales/invoices/add"} className="btn btn-primary">
                    <i className="simple-icon-plus me-2" aria-hidden="true"></i> Add Invoice
                </Link>
              </div>
            </div>
          </>
        )}
        
        {(pwaMode || (isMobile && !webViewMode)) && (
          <div className="col-12">
            <div className="pwa-search-section mb-3">
              <div className="input-group">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="form-control form-control-lg"
                  placeholder="Search invoices by number, party name..."
                />
                <button className="btn btn-outline-secondary" type="button">
                  <i className="simple-icon-magnifier"></i>
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="col-md-12">
          {(pwaMode || (isMobile && !webViewMode)) ? (
            // PWA Mobile Card Layout
            <div className="pwa-invoice-list">
              {pwaMode && mobilePWA && (
                <div className="pwa-pull-hint text-center text-muted mb-3">
                  <small>
                    <i className="simple-icon-arrow-down me-1"></i>
                    Pull down to refresh
                  </small>
                </div>
              )}
              {loading && <Loader></Loader>}
              {!loading && (
                <div>
                  {partyData.filter(row => 
                    !searchValue || 
                    row.invoice_no?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.prefix?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.party?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.total_amount?.toString().includes(searchValue)
                  ).map((row, key) => {
                    return (
                      <div key={key} className="pwa-invoice-card mb-3">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="card-title mb-1">
                                  {row.prefix}/{row.invoice_no}
                                </h6>
                                <small className="text-muted">ID: {row.id}</small>
                              </div>
                              <div className="pwa-status-badge">
                                <span className="badge bg-success">Completed</span>
                              </div>
                            </div>
                            
                            <div className="pwa-invoice-details mb-3">
                              <div className="row">
                                <div className="col-6">
                                  <small className="text-muted d-block">Party Name</small>
                                  <small>{row.party ? row.party.name : "N/A"}</small>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Date</small>
                                  <small>{row.date}</small>
                                </div>
                              </div>
                              <div className="row mt-2">
                                <div className="col-6">
                                  <small className="text-muted d-block">Amount</small>
                                  <small className="fw-bold text-primary">₹{row.total_amount}</small>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Quantity</small>
                                  <small>{row.total_qty} units</small>
                                </div>
                              </div>
                            </div>
                            
                            <div className="pwa-actions">
                              <div className="btn-group w-100" role="group">
                                <Link 
                                  to={"/invoice/view/" + row.id} 
                                  className="btn btn-outline-success btn-sm"
                                >
                                  <i className="simple-icon-printer me-1"></i> Print
                                </Link>
                                <Link 
                                  to={"/sales/invoices/edit/" + row.id} 
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="simple-icon-pencil me-1"></i> Edit
                                </Link>
                                <button 
                                  onClick={() => deleteInvoice(row)} 
                                  className="btn btn-outline-danger btn-sm"
                                >
                                  <i className="simple-icon-trash me-1"></i> Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {partyData.filter(row => 
                    !searchValue || 
                    row.invoice_no?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.prefix?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.party?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    row.total_amount?.toString().includes(searchValue)
                  ).length === 0 && (
                    <div className="text-center py-5">
                      <i className="simple-icon-doc" style={{fontSize: '3rem'}}></i>
                      <h5>No invoices found</h5>
                      <p className="text-muted">Start by creating your first invoice</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Original Desktop Table Layout
            <div className="card">
              <div className="card-body p-0">
                {loading && <Loader></Loader>}
                {!loading && (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Id</th>
                          <th>Invoice No</th>
                          <th>Name</th>
                          <th>Invoice Date</th>
                          <th>Amount</th>
                          <th>Qty</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partyData.filter(row => 
                          !searchValue || 
                          row.invoice_no?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                          row.prefix?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
                          row.party?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                          row.total_amount?.toString().includes(searchValue)
                        ).map((row, key) => {
                          return (
                            <tr key={key}>
                              <td>{row.id}</td>
                              <td>{row.prefix}/{row.invoice_no}</td>
                              <td>{row.party ? row.party.name : ""}</td>
                              <td>{row.date}</td>
                              <td>{row.total_amount}</td>
                              <td>{row.total_qty}</td>
                              <td>
                                <Link
                                  to={"/invoice/view/" + row.id}
                                  className="btn btn-sm btn-success mr-2 text-white"
                                >
                                  <i className="simple-icon-printer me-1"></i> Print
                                </Link>
                                <Link
                                  to={"/sales/invoices/edit/" + row.id}
                                  className="btn btn-sm btn-secondary mr-2"
                                >
                                  <i className="simple-icon-pencil me-1"></i> Edit
                                </Link>
                                <button
                                  onClick={() => deleteInvoice(row)}
                                  className="btn btn-sm btn-danger"
                                >
                                  <i className="simple-icon-trash me-1"></i> Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* PWA Floating Action Button */}
      {(pwaMode || (isMobile && !webViewMode)) && (
        <Link 
          to={"/sales/invoices/add"} 
          className="btn btn-primary pwa-fab position-fixed rounded-circle shadow-lg"
          style={{
            bottom: '100px',
            right: '20px',
            width: '60px',
            height: '60px',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
        >
          <i className="simple-icon-plus"></i>
        </Link>
      )}
    </>
  );
};

export default Invoices;
